/**
 * @type {SelectedDate}
 * Has `date` that was selected and `source`, that tells us where the selection came from.
 */
export class SelectedDate {
  date: string; // ISO String
  source: SelectionSource;
}

/**
 * DateSource is set depending on who set it. In most cases, it's the system itself, because it chooses an available date.
 */
export enum SelectionSource {
  Automatic = 'AUTOMATIC',
  UserSet = 'USER_SET',
}

export function initSelectedDate(date: string, source: SelectionSource): SelectedDate {
  return {
    date: date,
    source: source,
  };
}

/**
 * @param newDate
 * @param oldDate Previously selected date.
 *
 * This function ensures that a user selected date isn't accidentally overridden by the system. If that is desired behavior,
 * however, use `overrideUserSetDate(date)` instead.
 *
 */
export function setNewDateAutomatically(
  newDate: string,
  oldDate: SelectedDate | null
): SelectedDate {
  if (oldDate?.source === SelectionSource.UserSet) return oldDate;

  return { date: newDate, source: SelectionSource.Automatic };
}

/**
 * @param newDate Date that was selected by the user.
 *
 * Date has been selected by the user. If that's not the case, see setNewDateAutomatically.
 */
export function setDateByUser(newDate: string): SelectedDate {
  return { date: newDate, source: SelectionSource.UserSet };
}

/**
 * @param date Newly selected date by the system.
 *
 * Use this function when you want to override a user selected date. If that's not the case, use `setNewDateAutomatically`.
 */
export function overrideUserSetDate(date: string): SelectedDate {
  return { date: date, source: SelectionSource.Automatic };
}
